import { useEffect, useRef } from "react";

/**
 * Lembra onde a pessoa parou de rolar e volta pra lá quando ela reabre a tela.
 *
 * Pedido da Thainá (ago/2026): ela rola a lista de projetos, abre um projeto,
 * volta — e cai no topo, tendo que rolar tudo de novo até achar onde estava.
 *
 * DETALHE DO APP: quem rola NÃO é a janela. O conteúdo vive dentro de um
 * `flex-1 overflow-auto` do Layout, então `window.scrollY`/`scrollTo` não
 * servem. Por isso a busca sobe pelos pais a partir de uma âncora até achar o
 * primeiro elemento que de fato rola — assim continua funcionando se o Layout
 * mudar de estrutura.
 *
 * `pronto` deve virar true quando a lista já renderizou (dados carregados).
 * Restaurar antes disso não adianta: o container ainda é curto e o navegador
 * corta o scrollTop pra 0.
 *
 * Guarda em sessionStorage de propósito: a posição vale enquanto a aba estiver
 * aberta. Voltar dias depois e cair no meio da lista seria estranho.
 */
export function useLembrarScroll(chave: string, pronto: boolean) {
  const ancora = useRef<HTMLDivElement>(null);
  // Enquanto não restaurou, NÃO grava — senão o scroll 0 da montagem
  // sobrescreveria a posição que a gente ia restaurar.
  const restaurou = useRef(false);

  function containerDeRolagem(): HTMLElement | null {
    let el: HTMLElement | null = ancora.current?.parentElement ?? null;
    while (el) {
      const ov = getComputedStyle(el).overflowY;
      if ((ov === "auto" || ov === "scroll") && el.scrollHeight > el.clientHeight) return el;
      el = el.parentElement;
    }
    return null;
  }

  // RESTAURA (uma vez por montagem).
  useEffect(() => {
    if (!pronto || restaurou.current) return;
    let alvo = 0;
    try { alvo = Number(sessionStorage.getItem(chave) || 0) || 0; } catch { /* modo privado */ }
    if (alvo <= 0) { restaurou.current = true; return; }

    // O conteúdo pode ganhar altura em mais de um frame (imagens, listas
    // grandes). Tenta por alguns frames até o scrollTop "pegar".
    let tentativas = 0;
    let raf = 0;
    const tentar = () => {
      const c = containerDeRolagem();
      if (c) {
        c.scrollTop = alvo;
        if (Math.abs(c.scrollTop - alvo) < 2) { restaurou.current = true; return; }
      }
      if (++tentativas > 30) { restaurou.current = true; return; }
      raf = requestAnimationFrame(tentar);
    };
    raf = requestAnimationFrame(tentar);
    return () => cancelAnimationFrame(raf);
  }, [pronto, chave]);

  // SALVA enquanto rola (throttle por frame).
  useEffect(() => {
    const c = containerDeRolagem();
    if (!c) return;
    let raf = 0;
    const aoRolar = () => {
      if (!restaurou.current || raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        try { sessionStorage.setItem(chave, String(c.scrollTop)); } catch { /* modo privado */ }
      });
    };
    c.addEventListener("scroll", aoRolar, { passive: true });
    return () => {
      c.removeEventListener("scroll", aoRolar);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [chave, pronto]);

  return ancora;
}
